import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import SearchOffOutlinedIcon from '@mui/icons-material/SearchOffOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import {
  AppBar,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  IconButton,
  InputAdornment,
  LinearProgress,
  ListItemButton,
  Pagination,
  Skeleton,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import type { SubscriptionStatus } from '@prospero/shared';
import type {
  MembershipSearchFacetValue,
  MembershipSearchItem,
} from '@shared/dto/auth/membership-search.response.dto';
import { type RefObject, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getItalianProvinceName } from '../../config/italian-provinces';
import { useMembershipSearch } from '../../hooks/auth/use-membership-search';
import { useDebouncedValue } from '../../hooks/use-debounced-value';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { getSubscriptionStatusChipColor } from '../../pages/consultant/utils/subscription-status-color';
import { colors } from '../../theme/ai-theme';
import { SlideUpTransition } from '../transitions/slide-up-transition';
import { CompanyOptionContent } from './company-option-content';

const SEARCH_DEBOUNCE_MS = 300;
const PAGE_SIZE = 20;
const SKELETON_KEYS = ['a', 'b', 'c', 'd', 'e', 'f'];

export interface CompanyAdvancedSearchDialogProps {
  open: boolean;
  onClose: () => void;
  currentTenantId?: string | null;
  onSelect: (tenantId: string, tenantName: string) => void;
  /** Test seam: the default 300ms is what users get. */
  debounceMs?: number;
}

export function CompanyAdvancedSearchDialog({
  open,
  onClose,
  currentTenantId,
  onSelect,
  debounceMs = SEARCH_DEBOUNCE_MS,
}: CompanyAdvancedSearchDialogProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const resultsListRef = useRef<HTMLUListElement>(null);
  const resultsCountRef = useRef<HTMLParagraphElement>(null);
  const filters = useCompanySearchFilters(open, debounceMs);
  const { searchText, selectedStatuses, selectedProvince, page, hasActiveFilters } = filters;

  const {
    data: membershipSearch,
    isPending,
    isFetching,
    isError,
    refetch,
  } = useMembershipSearch(filters.query, {
    enabled: open,
  });

  const memberships = membershipSearch?.items ?? [];
  const searchStatus = resolveSearchStatus(isError, isPending);
  const total = membershipSearch?.total ?? 0;
  const pageCount = Math.ceil(total / PAGE_SIZE);
  const isEmptyResult = searchStatus === 'ready' && memberships.length === 0;
  const statusFacets = mergeSelected(membershipSearch?.facets.statuses ?? [], selectedStatuses);
  const provinceFacets = mergeSelected(
    membershipSearch?.facets.provinces ?? [],
    selectedProvince ? [selectedProvince] : [],
  );

  function handleSelect(membership: MembershipSearchItem) {
    if (membership.tenantId !== currentTenantId) {
      onSelect(membership.tenantId, membership.tenantName);
    }
    onClose();
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      aria-labelledby="company-advanced-search-title"
      slots={{ transition: SlideUpTransition }}
      slotProps={{ transition: { onEntered: () => searchInputRef.current?.focus() } }}
    >
      <AppBar position="sticky" elevation={1}>
        <Toolbar>
          <Typography
            id="company-advanced-search-title"
            variant="h6"
            component="h2"
            sx={{ flex: 1, fontWeight: 700, color: colors.navigation.text }}
          >
            {t('consultant.companyPicker.advancedTitle')}
          </Typography>
          <IconButton
            edge="end"
            onClick={onClose}
            aria-label={t('common.close')}
            sx={{
              width: 48,
              height: 48,
              color: colors.navigation.text,
              '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.1)' },
            }}
          >
            <CloseOutlinedIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Box
        sx={{
          boxSizing: 'border-box',
          width: '100%',
          minWidth: 0,
          maxWidth: 960,
          mx: 'auto',
          px: 2,
          py: 2,
          flex: 1,
        }}
      >
        <TextField
          fullWidth
          value={searchText}
          onChange={(event) => filters.changeSearchText(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'ArrowDown') {
              event.preventDefault();
              resultsListRef.current?.querySelector<HTMLElement>('[role="button"]')?.focus();
            }
          }}
          inputRef={searchInputRef}
          placeholder={t('consultant.companyPicker.searchPlaceholder')}
          slotProps={{
            htmlInput: {
              'aria-label': t('consultant.searchCompany'),
              style: { fontSize: 16 },
              enterKeyHint: 'search',
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlinedIcon aria-hidden />
                </InputAdornment>
              ),
              sx: { minHeight: 48 },
            },
          }}
        />

        <StatusFilterChips
          facets={statusFacets}
          selectedStatuses={selectedStatuses}
          onToggle={filters.toggleStatus}
        />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center', mt: 2 }}>
          <ProvinceFilter
            facets={provinceFacets}
            selectedProvince={selectedProvince}
            onChange={filters.changeProvince}
          />
          {hasActiveFilters && !isEmptyResult && (
            <Button sx={{ minHeight: 48 }} onClick={filters.resetFilters}>
              {t('consultant.companyPicker.resetFilters')}
            </Button>
          )}
        </Box>

        <Typography
          ref={resultsCountRef}
          tabIndex={-1}
          variant="body2"
          sx={{ color: 'text.secondary', mt: 2, mb: 1, outline: 'none', scrollMarginTop: 80 }}
          aria-live="polite"
        >
          {searchStatus === 'ready'
            ? t('consultant.companyPicker.resultsCount', { count: total })
            : ''}
        </Typography>
        {isFetching && searchStatus === 'ready' && <LinearProgress sx={{ mb: 1 }} />}

        <CompanySearchResults
          status={searchStatus}
          memberships={memberships}
          currentTenantId={currentTenantId}
          searchText={filters.query.search ?? ''}
          hasSelectedFilters={selectedStatuses.length > 0 || !!selectedProvince}
          listRef={resultsListRef}
          onRetry={() => refetch()}
          onClearSearch={() => filters.changeSearchText('')}
          onReset={filters.resetFilters}
          onSelect={handleSelect}
        />

        {pageCount > 1 && searchStatus === 'ready' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={pageCount}
              page={page}
              onChange={(_event, nextPage) => {
                filters.setPage(nextPage);
                resultsCountRef.current?.focus({ preventScroll: true });
                resultsCountRef.current?.scrollIntoView?.({ block: 'start' });
              }}
              color="primary"
              size={isMobile ? 'medium' : 'large'}
              siblingCount={isMobile ? 0 : 1}
              boundaryCount={1}
            />
          </Box>
        )}
      </Box>
    </Dialog>
  );
}

const resultsGridSx = {
  display: 'grid',
  // minmax(0, ...) lets the tracks shrink below the nowrap content width: with plain 1fr
  // a long name widened the grid past the viewport on phones.
  gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'repeat(2, minmax(0, 1fr))' },
  gap: 1.5,
};

function mergeSelected<TValue extends string>(
  facets: MembershipSearchFacetValue<TValue>[],
  selectedValues: TValue[],
): MembershipSearchFacetValue<TValue>[] {
  const missing = selectedValues
    .filter((value) => !facets.some((facet) => facet.value === value))
    .map((value) => ({ value, count: 0 }));
  return [...facets, ...missing];
}

interface CompanySearchResultsProps {
  status: 'error' | 'pending' | 'ready';
  memberships: MembershipSearchItem[];
  currentTenantId?: string | null;
  searchText: string;
  hasSelectedFilters: boolean;
  listRef: RefObject<HTMLUListElement | null>;
  onRetry: () => void;
  onClearSearch: () => void;
  onReset: () => void;
  onSelect: (membership: MembershipSearchItem) => void;
}

interface EmptyResultsProps {
  searchText: string;
  hasSelectedFilters: boolean;
  onClearSearch: () => void;
  onReset: () => void;
}

function EmptyResults({
  searchText,
  hasSelectedFilters,
  onClearSearch,
  onReset,
}: EmptyResultsProps) {
  const { t } = useTranslation();
  const message =
    searchText && !hasSelectedFilters
      ? t('consultant.companyPicker.noResultsForQuery', { query: searchText })
      : t('consultant.noSearchResults');
  const isTextOnly = searchText !== '' && !hasSelectedFilters;
  return (
    <Box sx={{ py: 4, textAlign: 'center' }}>
      <SearchOffOutlinedIcon aria-hidden sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
      <Typography sx={{ color: 'text.secondary', mb: 1.5, overflowWrap: 'anywhere' }}>
        {message}
      </Typography>
      {(isTextOnly || hasSelectedFilters) && (
        <Button
          variant="outlined"
          sx={{ minHeight: 48 }}
          onClick={isTextOnly ? onClearSearch : onReset}
        >
          {isTextOnly
            ? t('consultant.companyPicker.clearSearch')
            : t('consultant.companyPicker.resetFilters')}
        </Button>
      )}
    </Box>
  );
}

function CompanySearchResults({
  status,
  memberships,
  currentTenantId,
  searchText,
  hasSelectedFilters,
  listRef,
  onRetry,
  onClearSearch,
  onReset,
  onSelect,
}: CompanySearchResultsProps) {
  const { t } = useTranslation();
  if (status === 'error') {
    return (
      <Box role="alert" sx={{ py: 4, textAlign: 'center' }}>
        <Typography sx={{ color: 'error.main', mb: 1.5 }}>
          {t('consultant.companyPicker.loadError')}
        </Typography>
        <Button variant="outlined" sx={{ minHeight: 48 }} onClick={onRetry}>
          {t('consultant.companyPicker.retry')}
        </Button>
      </Box>
    );
  }
  if (status === 'pending') {
    return (
      <Box role="status" aria-label={t('common.loading')} sx={resultsGridSx}>
        {SKELETON_KEYS.map((key) => (
          <Skeleton key={key} variant="rounded" height={64} />
        ))}
      </Box>
    );
  }
  if (memberships.length === 0) {
    return (
      <EmptyResults
        searchText={searchText}
        hasSelectedFilters={hasSelectedFilters}
        onClearSearch={onClearSearch}
        onReset={onReset}
      />
    );
  }
  return (
    <Box component="ul" ref={listRef} sx={{ ...resultsGridSx, listStyle: 'none', m: 0, p: 0 }}>
      {memberships.map((membership) => {
        const isCurrent = membership.tenantId === currentTenantId;
        return (
          <Box component="li" key={membership.tenantId} sx={{ minWidth: 0 }}>
            <ListItemButton
              onClick={() => onSelect(membership)}
              selected={isCurrent}
              aria-current={isCurrent ? 'true' : undefined}
              sx={{
                minHeight: 64,
                minWidth: 0,
                border: `1px solid ${colors.border.light}`,
                borderRadius: 2,
              }}
            >
              <CompanyOptionContent membership={membership} isCurrent={isCurrent} />
            </ListItemButton>
          </Box>
        );
      })}
    </Box>
  );
}

interface StatusFilterChipsProps {
  facets: MembershipSearchFacetValue<SubscriptionStatus>[];
  selectedStatuses: SubscriptionStatus[];
  onToggle: (status: SubscriptionStatus) => void;
}

function StatusFilterChips({ facets, selectedStatuses, onToggle }: StatusFilterChipsProps) {
  const { t } = useTranslation();

  function statusLabel(status: SubscriptionStatus): string {
    if (status === 'TRIALING') {
      return t('consultant.trialChip');
    }
    return t(`consultant.clients.status.${status}`, { defaultValue: status });
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: { xs: 'nowrap', sm: 'wrap' },
        overflowX: 'auto',
        gap: 1,
        mt: 2,
        scrollbarWidth: 'none',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
      role="group"
      aria-label={t('consultant.companyPicker.statusFilter')}
    >
      {facets.map((facet) => {
        const status = facet.value;
        const isSelected = selectedStatuses.includes(status);
        const legendColor = getSubscriptionStatusChipColor(status);
        return (
          <Chip
            key={status}
            label={t('consultant.companyPicker.statusChipLabel', {
              label: statusLabel(status),
              count: facet.count,
            })}
            color={isSelected ? 'primary' : 'default'}
            variant={isSelected ? 'filled' : 'outlined'}
            icon={
              isSelected ? (
                <CheckOutlinedIcon aria-hidden />
              ) : (
                <Box
                  aria-hidden
                  component="span"
                  sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: `${legendColor}.main` }}
                />
              )
            }
            clickable
            aria-pressed={isSelected}
            onClick={() => onToggle(status)}
            sx={{
              minHeight: 48,
              flexShrink: 0,
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              '& .MuiChip-icon': { ml: 1.5, mr: -0.5 },
            }}
          />
        );
      })}
    </Box>
  );
}

interface ProvinceFilterProps {
  facets: MembershipSearchFacetValue[];
  selectedProvince: string | null;
  onChange: (province: string | null) => void;
}

function formatProvinceLabel(provinceCode: string): string {
  const provinceName = getItalianProvinceName(provinceCode);
  return provinceName ? `${provinceCode} · ${provinceName}` : provinceCode;
}
function ProvinceFilter({ facets, selectedProvince, onChange }: ProvinceFilterProps) {
  const { t } = useTranslation();
  return (
    <Autocomplete<MembershipSearchFacetValue, false, false, false>
      sx={{ flex: '1 1 240px', maxWidth: { md: 360 } }}
      options={facets}
      value={facets.find((facet) => facet.value === selectedProvince) ?? null}
      onChange={(_event, facet) => onChange(facet?.value ?? null)}
      getOptionLabel={(facet) => formatProvinceLabel(facet.value)}
      renderOption={({ key, ...optionProps }, facet) => {
        const provinceLabel = formatProvinceLabel(facet.value);
        return (
          <Box
            component="li"
            key={key}
            {...optionProps}
            aria-label={`${provinceLabel} (${facet.count})`}
            sx={{ display: 'flex', gap: 1.5, minHeight: 48 }}
          >
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              <Box component="span" sx={{ fontWeight: 700 }}>
                {facet.value}
              </Box>
              {provinceLabel !== facet.value && (
                <Box component="span" sx={{ color: 'text.secondary' }}>
                  {provinceLabel.slice(facet.value.length)}
                </Box>
              )}
            </Box>
            <Box
              component="span"
              sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
            >
              {facet.count}
            </Box>
          </Box>
        );
      }}
      isOptionEqualToValue={(option, candidate) => option.value === candidate.value}
      noOptionsText={t('consultant.noSearchResults')}
      renderInput={(params) => (
        <TextField
          {...params}
          label={t('consultant.companyPicker.provinceFilter')}
          slotProps={{
            ...params.slotProps,
            htmlInput: { ...params.slotProps.htmlInput, style: { fontSize: 16 } },
          }}
        />
      )}
    />
  );
}

function resolveSearchStatus(isError: boolean, isPending: boolean): 'error' | 'pending' | 'ready' {
  if (isError) {
    return 'error';
  }
  return isPending ? 'pending' : 'ready';
}

function useCompanySearchFilters(open: boolean, debounceMs: number) {
  const [searchText, setSearchText] = useState('');
  const [selectedStatuses, setSelectedStatuses] = useState<SubscriptionStatus[]>([]);
  const [selectedProvince, setSelectedProvince] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(searchText.trim(), debounceMs);

  function resetFilters() {
    setSearchText('');
    setSelectedStatuses([]);
    setSelectedProvince(null);
    setPage(1);
  }

  useEffect(() => {
    if (!open) {
      setSearchText('');
      setSelectedStatuses([]);
      setSelectedProvince(null);
      setPage(1);
    }
  }, [open]);

  function toggleStatus(status: SubscriptionStatus) {
    setSelectedStatuses((current) =>
      current.includes(status) ? current.filter((entry) => entry !== status) : [...current, status],
    );
    setPage(1);
  }

  function changeSearchText(nextText: string) {
    setSearchText(nextText);
    setPage(1);
  }

  function changeProvince(province: string | null) {
    setSelectedProvince(province);
    setPage(1);
  }

  return {
    searchText,
    selectedStatuses,
    selectedProvince,
    page,
    hasActiveFilters: searchText.trim() !== '' || selectedStatuses.length > 0 || !!selectedProvince,
    query: {
      search: debouncedSearch || undefined,
      subscriptionStatus: selectedStatuses.length > 0 ? selectedStatuses : undefined,
      provincia: selectedProvince ?? undefined,
      page,
      pageSize: PAGE_SIZE,
    },
    setPage,
    resetFilters,
    toggleStatus,
    changeSearchText,
    changeProvince,
  };
}
